/**
 * The guest replay's `INSERT OR IGNORE` skips the guest's `user` row when the account already has
 * one: both carry the same id (`ACTOR_USER_ID`), and the account's row wins like every other
 * replayed row. Settings are the account's to keep, but level packs the guest authored are user
 * content, so they are folded into the account's row here, inside the replay transaction. An
 * account copy of a pack (same pack id) keeps precedence.
 *
 * Does nothing when the guest's row itself became the account's row, or when it has no packs.
 */
import { customLevelPacksSchema } from "@-/dev.improve.haniverse.lib.level.custom";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

export interface RetainGuestLevelPacksTransaction {
  getFirstAsync<T>(sql: string, params: (number | string)[]): Promise<T | null>;
  runAsync(sql: string, params: (number | string)[]): Promise<unknown>;
}

const RETAIN_DEADLINE_MS = 30_000;

export async function retainGuestCustomLevelPacks(
  transaction: RetainGuestLevelPacksTransaction,
  guestCustomLevelPacks: string | null | undefined,
): Promise<void> {
  if (guestCustomLevelPacks === null || guestCustomLevelPacks === undefined) {
    return;
  }
  const guest = customLevelPacksSchema.parse(JSON.parse(guestCustomLevelPacks));
  if (guest.packs.length === 0) {
    return;
  }
  const account = await withDeadline(
    { deadline: RETAIN_DEADLINE_MS, operation: "read the account's level packs" },
    () =>
      transaction.getFirstAsync<{ custom_level_packs: string; id: string }>(
        'SELECT "id", "custom_level_packs" FROM "user" ORDER BY "created_at", "id" LIMIT 1',
        [],
      ),
  );
  if (account === null) {
    return;
  }
  const document = customLevelPacksSchema.parse(JSON.parse(account.custom_level_packs));
  const accountPackIds = new Set(document.packs.map((pack) => pack.id));
  const missing = guest.packs.filter((pack) => !accountPackIds.has(pack.id));
  if (missing.length === 0) {
    return;
  }
  await withDeadline({ deadline: RETAIN_DEADLINE_MS, operation: "retain guest level packs" }, () =>
    transaction.runAsync('UPDATE "user" SET "custom_level_packs" = ? WHERE "id" = ?', [
      JSON.stringify({ ...document, packs: [...document.packs, ...missing] }),
      account.id,
    ]),
  );
}
